import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { CONTACTS } from "./data";
import EnquiryForm from "./EnquiryForm";

const EASE = [0.16, 1, 0.3, 1];

const CHANNELS = [
  {
    icon: Phone,
    label: "Call or text us",
    value: CONTACTS.phone,
    href: CONTACTS.phoneHref,
    testid: "direct-contact-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACTS.email,
    href: CONTACTS.emailHref,
    testid: "direct-contact-email",
  },
];

export default function Contact() {
  return (
    <section id="contact" className="py-24 lg:py-32 bg-sand/60 border-t border-[#E5DDD0]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="lg:col-span-7"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-clay font-semibold mb-6">
            Contact & Enquiries
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-snug text-ink mb-6">
            Begin with
            <span className="italic text-clay"> a conversation.</span>
          </h2>
          <p className="text-base text-ink/70 leading-relaxed mb-10 max-w-md">
            Tell us about your skin goals and we'll reply within 24 hours with
            honest guidance — never a hard sell.
          </p>
          <div className="bg-cream border border-[#E5DDD0] rounded-3xl p-8 lg:p-10">
            <EnquiryForm idPrefix="enquiry" />
          </div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="lg:col-span-5 flex flex-col gap-4 lg:pt-32"
        >
          {CHANNELS.map((c) => (
            <a
              key={c.label}
              data-testid={c.testid}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="group flex items-center gap-5 bg-cream border border-[#E5DDD0] rounded-2xl px-6 py-5 hover:border-clay/50 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-full bg-clay/10 flex items-center justify-center shrink-0 group-hover:bg-clay/20 transition-colors duration-300">
                <c.icon size={18} className="text-clay" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-ink/45 font-semibold">
                  {c.label}
                </span>
                <span className="block text-sm font-semibold text-ink mt-1">
                  {c.value}
                </span>
              </span>
            </a>
          ))}
          <div className="flex items-center gap-5 bg-cream border border-[#E5DDD0] rounded-2xl px-6 py-5">
            <span className="w-11 h-11 rounded-full bg-sage/15 flex items-center justify-center shrink-0">
              <MapPin size={18} className="text-sage" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-[0.2em] text-ink/45 font-semibold">
                Visit the clinic
              </span>
              <span className="block text-sm font-semibold text-ink mt-1">
                {CONTACTS.address}
              </span>
            </span>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}
